import React from "react";
import { Grid, Typography } from "@mui/material";
import DetailRow from "../../pages/common-features/DetailRow";
import { useSelector } from "react-redux";
import { RootState } from "../../services/redux/store";
import { camelToTitle } from "../Form/StepperDyanmicForm/helper";
import SummarySectionHeader from "./SummarySectionHeader";

interface EmploymentDetailsTabProps {
  employmentDetails: any[];
  companyAddressDetails: any[];
  isMobile: boolean;
  designationsVal?: any;
  pageType?: string;
}

const EmploymentDetailsTab: React.FC<EmploymentDetailsTabProps> = ({
  employmentDetails,
  companyAddressDetails,
  isMobile,
  designationsVal,
  pageType = "DEFAULT",
}) => {
  const { fetchUserApplicationData } = useSelector(
    (state: RootState) => state.stepper
  );

  const isCompanyAddressField = (field: any) => {
    const combined = `${field?.key || field?.name || ""} ${field?.label || ""}`.toLowerCase();

    return (
      /companyaddress[123]|addressline[123]|address line [123]|landmark|pincode|city|state|country/.test(
        combined
      ) && !/companyname|company name/.test(combined)
    );
  };

  const getCompanyAddressFieldOrder = (field: any) => {
    const combined = `${field?.key || field?.name || ""} ${field?.label || ""}`.toLowerCase();

    if (/companyaddress1|addressline1|address line 1/.test(combined)) return 1;
    if (/companyaddress2|addressline2|address line 2/.test(combined)) return 2;
    if (/companyaddress3|addressline3|address line 3/.test(combined)) return 3;
    if (/landmark/.test(combined)) return 4;
    if (/pincode/.test(combined)) return 5;
    if (/city/.test(combined)) return 6;
    if (/state/.test(combined)) return 7;
    if (/country/.test(combined)) return 8;
    return 99;
  };

  const getEmploymentDisplayLabel = (field: any) => {
    const combined = `${field?.key || field?.name || ""} ${field?.label || ""}`.toLowerCase();

    if (/employmenttype|employment type/.test(combined)) return "Type";
    if (/employmentyear|employment year/.test(combined)) return "Year";
    if (/employmentmonth|employment month/.test(combined)) return "Month";
    if (/companyname|company name/.test(combined)) return "Company Name";
    return field.label;
  };

  const getCompanyAddressDisplayLabel = (field: any) => {
    const combined = `${field?.key || field?.name || ""} ${field?.label || ""}`.toLowerCase();

    if (/companyaddress1|addressline1|address line 1/.test(combined)) return "Address Line 1";
    if (/companyaddress2|addressline2|address line 2/.test(combined)) return "Address Line 2";
    if (/companyaddress3|addressline3|address line 3/.test(combined)) return "Address Line 3";
    if (/landmark/.test(combined)) return "Landmark";
    if (/pincode/.test(combined)) return "Pincode";
    if (/city/.test(combined)) return "City";
    if (/state/.test(combined)) return "State";
    if (/country/.test(combined)) return "Country";
    return field.label;
  };

  // Helper to map fields from the vertical step to the required format
  function mapEmploymentDetailsFromVerticalStep(verticalStep: any) {
    if (!verticalStep || !verticalStep?.fields) return [];
    return verticalStep?.fields.map((fieldObj: any) => {
      const name = Object.keys(fieldObj)?.[0];
      const value = fieldObj?.[name];
      let label = name;
      // Remove 'Employment' prefix for employment details
      if (name.toLowerCase().startsWith("employment")) {
        label = label.replace(/^employment/, "");
      }
      // Remove 'Company' prefix for company address details
      if (name.toLowerCase().includes("company")) {
        label = label.replace(/company/i, "");
      }
      // Capitalize each word and add space before trailing numbers
      label = label.replace(/([A-Z])/g, " $1").trim();
      label = label.replace(/(\D)(\d+)$/, "$1 $2");
      label = label.replace(/^./, (str) => str.toUpperCase());
      label = label
        .split(" ")
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(" ");
      // Mark if this is a company address field
      const isCompanyField = isCompanyAddressField({ name, label });
      return {
        label,
        name,
        value,
        isCompanyField,
      };
    });
  }

  const mergeFieldArrays = (primaryFields: any[] = [], fallbackFields: any[] = []) => {
    const normalizeKey = (field: any) =>
      String(field?.name ?? field?.key ?? "").toLowerCase();

    const primaryMap = new Map<string, any>();
    primaryFields.forEach((field) => {
      const key = normalizeKey(field);
      if (key) primaryMap.set(key, field);
    });

    const fallbackMap = new Map<string, any>();
    fallbackFields.forEach((field) => {
      const key = normalizeKey(field);
      if (key) fallbackMap.set(key, field);
    });

    const merged = fallbackFields.map((fallbackField) => {
      const key = normalizeKey(fallbackField);
      const primaryField = key ? primaryMap.get(key) : undefined;
      return primaryField
        ? { ...fallbackField, ...primaryField, value: primaryField?.value ?? "" }
        : { ...fallbackField, value: fallbackField?.value ?? "" };
    });

    primaryFields.forEach((primaryField) => {
      const key = normalizeKey(primaryField);
      if (!key || fallbackMap.has(key)) return;
      merged.push({ ...primaryField, value: primaryField?.value ?? "" });
    });

    return merged;
  };

  let employmentDetailsToShow = employmentDetails;
  let companyAddressDetailsToShow = companyAddressDetails;
  if (
    fetchUserApplicationData &&
    fetchUserApplicationData?.partnerStartedJourney &&
    employmentDetailsToShow?.length === 0 &&
    companyAddressDetailsToShow?.length === 0
  ) {
    employmentDetailsToShow = employmentDetails || [];
    companyAddressDetailsToShow = companyAddressDetails || [];
  }

  if (
    pageType === "SUMMARY" &&
    fetchUserApplicationData &&
    !fetchUserApplicationData?.partnerStartedJourney
  ) {
    // Find the 'Applicant & Loan Details' horizontal step
    const applicantLoanStep = fetchUserApplicationData?.horizontalSteps?.find(
      (h: any) => h.horizontalStepName === "Applicant & Loan Details"
    );
    // Find the 'Employment Details' vertical step
    const employmentDetailsStep = applicantLoanStep?.verticalSteps?.find(
      (v: any) => v.verticalStepName === "Employment Details"
    );
    const fallbackEmploymentDetails = employmentDetails || [];
    const fallbackCompanyAddress = companyAddressDetails || [];
    const allFields = mapEmploymentDetailsFromVerticalStep(
      employmentDetailsStep
    );
    const mappedEmploymentDetails = allFields?.filter(
      (field: any) => !field.isCompanyField
    );
    const code = allFields.find(
      (f:any) => f?.name === "employmentDesignation"
    )?.value;

  const fullName =
    designationsVal?.find((d:any) => d?.genDesignation === code)?.genDesgDesc ?? code;

  employmentDetailsToShow = (mappedEmploymentDetails || []).map((item: any) =>
    item.name === "employmentDesignation"
      ? { ...item, value: fullName }
      : item
  );
      let companyFields = allFields?.filter((field: any) => field.isCompanyField);
      companyAddressDetailsToShow = mergeFieldArrays(
        companyFields,
        fallbackCompanyAddress
      );
      employmentDetailsToShow = mergeFieldArrays(
        employmentDetailsToShow,
        fallbackEmploymentDetails
      );
  } else {
    employmentDetailsToShow = mergeFieldArrays([], employmentDetails || []);
    companyAddressDetailsToShow = mergeFieldArrays(
      [],
      companyAddressDetails || []
    );
  }

  const employmentAddressFields = employmentDetailsToShow.filter(isCompanyAddressField);
  const companyAddressOnlyFields = companyAddressDetailsToShow.filter(isCompanyAddressField);
  const misplacedEmploymentFields = companyAddressDetailsToShow.filter(
    (field: any) => !isCompanyAddressField(field)
  );

  employmentDetailsToShow = mergeFieldArrays(
    employmentDetailsToShow.filter((field: any) => !isCompanyAddressField(field)),
    misplacedEmploymentFields
  );
  companyAddressDetailsToShow = mergeFieldArrays(
    employmentAddressFields,
    companyAddressOnlyFields
  ).sort(
    (a: any, b: any) =>
      getCompanyAddressFieldOrder(a) - getCompanyAddressFieldOrder(b)
  );

  // Determine whether to hide the Company Address section
  const isEmployedField = employmentDetailsToShow?.find(
    (f: any) => f.name === "employmentisEmployed"
  );
  const employmentTypeField = employmentDetailsToShow?.find(
    (f: any) => f.name === "employmentType"
  );
  const isNotEmployed =
    isEmployedField &&
    String(isEmployedField.value).toLowerCase() === "no";
  const isOthersType = employmentTypeField?.value === "Others";
  const hideCompanyAddress = Boolean(isNotEmployed || isOthersType);

  if (!employmentDetailsToShow || employmentDetailsToShow.length === 0) {
    return (
      <Grid item xs={12}>
        <Typography variant="body2" color="textSecondary">
          No details available.
        </Typography>
      </Grid>
    );
  }
  const isEmploymentTypeOthers =
    employmentDetailsToShow?.find((item: any) => item.name === 'employmentType')
      ?.value === 'Others';

  return (
    <>
      <Grid item xs={12} sx={{ mx: isMobile ? 1 : 0, mt: 2, mb: 1 }}>
        <SummarySectionHeader title="Employment Details" />
      </Grid>
      {employmentDetailsToShow
        ?.filter((field: any) => {
          if (isNotEmployed) {
            return /^employmentisEmployed$/i.test(field.name);
          }
          if (
            isEmploymentTypeOthers &&
            /employment(Designation|Year|Month)$/i.test(field.name)
          ) {
            return false;
          }
          return true;
        })
        .map((field: any, subIndex: number) => (
          <Grid item xs={12} sm={6} md={3} key={subIndex}>
            <DetailRow
              item={{
	                label: getEmploymentDisplayLabel(field),
                value:
                  typeof field.value === "boolean"
                    ? field.value
                      ? "Yes"
                      : "No"
                    : typeof field.value === "string" &&
                      ["true", "false"].includes(field.value?.toLowerCase?.())
                    ? field.value.toLowerCase() === "true"
                      ? "Yes"
                      : "No"
                    : field.value,
                isVerified: false,
              }}
            />
          </Grid>
        ))}

      {!hideCompanyAddress && (
        <>
          <Grid
            item
            xs={12}
            sx={{ mx: isMobile ? 1 : 0, mt: isMobile ? 5 : 4, mb: 1 }}
          >
            <SummarySectionHeader title="Company Address" />
          </Grid>
          {companyAddressDetailsToShow?.map((field: any, subIndex: number) => (
            <Grid item xs={12} sm={6} md={3} key={subIndex}>
              <DetailRow
	                item={{ label: getCompanyAddressDisplayLabel(field), value: field.value, isVerified: false }}
              />
            </Grid>
          ))}
        </>
      )}
    </>
  );
};

export default EmploymentDetailsTab;
